import { useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  createJournal,
  resetChartOfAccountStatus,
} from "../../../redux/slice/accounting/accountingSlice";
import Select from "../../form/select/Select";
import { flashMessage } from "../../../utils/flash";

const validationSchema = Yup.object().shape({
  entry_date: Yup.string().required("Booking date is required"),
});

const Journal = ({ modal_id }) => {
  const { user } = useSelector((state) => state?.auth?.user);
  const { all_accounts, isUpdated } = useSelector((state) => state?.accounting);

  const { offices } = useSelector((state) => state?.office?.offices);
  const [debit, setDebit] = useState([
    {
      d_account_id: "",
      d_office_id: "",
      d_amount: "",
    },
  ]);

  const [credit, setCredit] = useState([
    {
      c_account_id: "",
      c_office_id: "",
      c_amount: "",
    },
  ]);
  const [debitTotal, setDebitTotal] = useState("");
  const [creditTotal, setCreditTotal] = useState("");
  const closeJournal = useRef();
  const dAccountType = useRef();
  const dOffice = useRef();
  const cOffice = useRef();
  const cAccountType = useRef();
  const dispatch = useDispatch();

  const handleDebitDetails = (value, index, name) => {
    let data = [...debit];
    data[index][name] = value;
    setDebit(data);
  };

  const handleCreditDetails = (value, index, name) => {
    let data = [...credit];
    data[index][name] = value;
    setCredit(data);
  };

  const addDebit = () => {
    let obj = {
      d_account_id: "",
      d_office_id: "",
      d_amount: "",
    };
    setDebit([...debit, obj]);
  };

  const removeDebit = (index) => {
    if (debit?.length > 1) {
      let data = [...debit];
      data.splice(index, 1);
      setDebit(data);
    }
  };

  const removeCredit = (index) => {
    if (credit?.length > 1) {
      let data = [...credit];
      data.splice(index, 1);
      setCredit(data);
    }
  };

  const addCredit = () => {
    let obj = {
      c_account_id: "",
      c_office_id: "",
      c_amount: "",
    };
    setCredit([...credit, obj]);
  };

  const DebitCreditCalc = useCallback(() => {
    const calc = (array) => {
      let totalAmount = array.reduce((acc, currentValue) => {
        let key = Object.keys(currentValue)[2];
        if (currentValue[key]) {
          return acc + parseFloat(currentValue[key]);
        }
        return acc;
      }, 0);
      const debitInKobo = Math.round(totalAmount * 100); // convert to kobo and round to the nearest integer
      const debitInNaira = (debitInKobo / 100).toFixed(2); // convert to naira and format to two decimal places
      return debitInNaira;
    };

    setDebitTotal(calc(debit));
    setCreditTotal(calc(credit));
  }, [debit, credit]);

  const onSubmit = (values, { resetForm }) => {
     // Check if credit and debit arrays are aligned
      const isOfficeIdAligned = credit.every((creditItem, index) => {
        const debitItem = debit[index];
        return (
          debitItem && creditItem.c_office_id === debitItem.d_office_id
        );
      });

      if (!isOfficeIdAligned) {
        flashMessage("info","Credit and Debit office must match for corresponding entries.");
        return;
      }
    let payload = {
      ...values,
      id: user?.staff_id,
      credit_arrays: [...credit],
      debit_arrays: [...debit],
    };

    

    if (debitTotal === creditTotal) {
      dispatch(createJournal(payload));
    }
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      entry_date: "",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  const handleCloseJournal = (event) => {
    dAccountType.current?.clearValue();
    dOffice.current?.clearValue();
    cOffice.current?.clearValue();
    cAccountType.current?.clearValue();
    setDebit([
      {
        d_account_id: "",
        d_office_id: "",
        d_amount: "",
      },
    ]);
    setCredit([
      {
        c_account_id: "",
        c_office_id: "",
        c_amount: "",
      },
    ]);
    resetForm();
  };

  useEffect(() => {
    if (isUpdated) {
      closeJournal.current.click();
    }
    dispatch(resetChartOfAccountStatus());
  }, [dispatch, isUpdated]);

  useEffect(() => {
    DebitCreditCalc();
  }, [DebitCreditCalc]);

  return (
    <>
      <div
        className='modal fade'
        id={modal_id}
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        tabIndex='-1'
        aria-labelledby='staticBackdropLabel'
        aria-hidden='true'
      >
        <div
          className='modal-dialog modal-dialog-scrollable modal-lg'
          role='document'
        >
          <form className='modal-content rounded-4' onSubmit={handleSubmit}>
            <div className='modal-header'>
              <h4 className='modal-title text-primary-1' id='myLargeModalLabel'>
                Create Journal Entry
              </h4>
              <span
                type='button'
                className=' bg-gray hover rounded-circle border-none modal1'
                data-bs-dismiss='modal'
                aria-hidden='true'
                ref={closeJournal}
                style={{ padding: "0px 3px" }}
                onClick={handleCloseJournal}
              >
                <i
                  className='mdi mdi-close text-white'
                  style={{ fontSize: "16px" }}
                ></i>
              </span>
            </div>
            <div className='modal-body'>
              <div className='row'>
                <div className='col-md-5'>
                  <div className='ms-3'>
                    <button
                      type=''
                      className='btn btn-primary btn-sm rounded-4 d-flex justify-content-center align-items-center gap-1'
                      onClick={addDebit}
                    >
                      <i className='mdi mdi-plus'></i>
                      <span>Debit</span>
                    </button>
                  </div>
                  {debit?.map((item, i) => {
                    return (
                      <div className='ms-2 mt-3' key={i}>
                        <div className='mb-2'>
                          <label htmlFor='account' className='form-label'>
                            Account <span className='text-danger'>*</span>
                          </label>

                          <Select
                            ref={dAccountType}
                            isError={errors?.account_id && touched?.account_id}
                            name='d_account_id'
                            id='d_account_id'
                            data={all_accounts?.filterd_account?.map(
                              (item, i) => ({
                                label: item?.account_name,
                                value: item?.id,
                              })
                            )}
                            value={item?.d_account_id}
                            onChange={(selectedValues) =>
                              handleDebitDetails(
                                selectedValues?.value,
                                i,
                                "d_account_id"
                              )
                            }
                          />
                        </div>
                        <div className='mb-2'>
                          <label htmlFor='account' className='form-label'>
                            Office <span className='text-danger'>*</span>
                          </label>
                          <Select
                            ref={dOffice}
                            name='d_office_id'
                            data={offices?.map((item) => ({
                              label: item?.office_name,
                              value: item?.id,
                            }))}
                            value={values?.office_id}
                            onChange={(selectedValues) =>
                              handleDebitDetails(
                                selectedValues?.value,
                                i,
                                "d_office_id"
                              )
                            }
                          />
                        </div>
                        <div className='mb-2'>
                          <label htmlFor='d_amount' className='form-label'>
                            Amount <span className='text-danger'>*</span>
                          </label>
                          <input
                            type='number'
                            name='d_amount'
                            id='d_amount'
                            placeholder='0.00'
                            value={item?.d_amount}
                            className='form-control form-control-sm'
                            onChange={(e) =>
                              handleDebitDetails(e.target.value, i, "d_amount")
                            }
                          />
                          {debit?.length > 1 && (
                            <div
                              className={`border border-bottom-3 border-end-0 border-top-0 border-start-0 border-info d-flex justify-content-end cursor-p ${
                                i === 0 ? "mt-3" : ""
                              }`}
                            >
                              <span className='' onClick={() => removeDebit(i)}>
                                <i className='mdi mdi-delete text-danger fs-4 '></i>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className='col-md-2'></div>
                <div className='col-md-5'>
                  <div className='d-flex justify-content-end me-3'>
                    <button
                      type=''
                      className='btn btn-primary btn-sm rounded-4 d-flex justify-content-center align-items-center gap-1'
                      onClick={addCredit}
                    >
                      <i className='mdi mdi-plus'></i>
                      <span>Credit</span>
                    </button>
                  </div>
                  <div className='mx-2 mt-3 '>
                    {credit?.map((item, i) => {
                      return (
                        <div key={i}>
                          <div className='mb-2'>
                            <label htmlFor='account' className='form-label'>
                              Account <span className='text-danger'>*</span>
                            </label>

                            <Select
                              ref={cAccountType}
                              isError={
                                errors?.account_id && touched?.account_id
                              }
                              name='c_account_id'
                              data={all_accounts?.filterd_account?.map(
                                (item, i) => ({
                                  label: item?.account_name,
                                  value: item?.id,
                                })
                              )}
                              value={item?.c_account_id}
                              onChange={(selectedValues) =>
                                handleCreditDetails(
                                  selectedValues?.value,
                                  i,
                                  "c_account_id"
                                )
                              }
                            />
                          </div>
                          <div className='mb-2'>
                            <label htmlFor='account' className='form-label'>
                              Office <span className='text-danger'>*</span>
                            </label>
                            <Select
                              ref={cOffice}
                              name='c_office_id'
                              data={offices?.map((item) => ({
                                label: item?.office_name,
                                value: item?.id,
                              }))}
                              value={item?.c_office_id}
                              onChange={(selectedValues) =>
                                handleCreditDetails(
                                  selectedValues?.value,
                                  i,
                                  "c_office_id"
                                )
                              }
                            />
                          </div>
                          <div className='mb-2'>
                            <label htmlFor='account' className='form-label'>
                              Amount <span className='text-danger'>*</span>
                            </label>
                            <input
                              type='number'
                              name='c_amount'
                              id='c_amount'
                              placeholder='0.00'
                              value={item?.c_amount}
                              className='form-control form-control-sm'
                              onChange={(e) =>
                                handleCreditDetails(
                                  e.target.value,
                                  i,
                                  "c_amount"
                                )
                              }
                            />
                            {credit?.length > 1 && (
                              <div
                                className={`border border-bottom-3 border-end-0 border-top-0 border-start-0 border-info d-flex justify-content-end cursor-p ${
                                  i === 0 ? "mt-3" : ""
                                }`}
                              >
                                <span
                                  className=''
                                  onClick={() => removeCredit(i)}
                                >
                                  <i className='mdi mdi-delete text-danger fs-4 '></i>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-md-6'>
                  <div className='mx-2'>
                    <label
                      htmlFor=''
                      className={`form-label ${
                        debitTotal === creditTotal ? "" : "text-danger"
                      }`}
                    >
                      Total Debit
                    </label>
                    <input
                      type='text'
                      className={`form-control form-control-sm ${
                        debitTotal === creditTotal ? "" : "is-invalid"
                      }`}
                      placeholder='0.00'
                      readOnly
                      value={debitTotal}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='mx-2'>
                    <label
                      htmlFor=''
                      className={`form-label  ${
                        debitTotal === creditTotal ? "" : "text-danger"
                      }`}
                    >
                      Total Credit
                    </label>
                    <input
                      type='text'
                      className={`form-control form-control-sm ${
                        debitTotal || creditTotal
                          ? debitTotal === creditTotal
                            ? ""
                            : "is-invalid"
                          : ""
                      }`}
                      placeholder='0.00'
                      value={creditTotal}
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-md-12'>
                  <div className='mx-2 mt-3'>
                    <label htmlFor='' className='form-label'>
                      Transaction Date <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='date'
                      name='entry_date'
                      className={`form-control form-control-sm ${
                        errors.entry_date && touched.entry_date
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder='0.00'
                      value={values?.entry_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors.entry_date && touched.entry_date && (
                      <span className='text-danger'>{errors.entry_date}</span>
                    )}
                  </div>
                </div>
                <div className='col-md-12'>
                  <div className='mx-2 mt-3'>
                    <label htmlFor='' className='form-label'>
                      Description
                    </label>
                    <textarea
                      name='description'
                      id='description'
                      cols='30'
                      rows='4'
                      className='form-control form-control-sm'
                      placeholder='Enter description here'
                      value={values?.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-footer me-3'>
              <button
                type='button'
                className='btn btn-secondary btn-sm rounded-3'
                data-bs-dismiss='modal'
                onClick={handleCloseJournal}
              >
                Close
              </button>
              <button
                type='submit'
                className='btn btn-primary btn-sm rounded-3'
              >
                Add
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Journal;
